/**
 * installField — the installer app's data (FIELD-CREWS-01 phase 1, Seth
 * 2026-09-24). What the crew records ON SITE, beside the office's job:
 *
 *   · the money the installer may see — contract, add-ons, paid, balance
 *     (decision 4: customer prices + the balance, never cost or margin);
 *   · each unit confirmed going in — the warehouse's serial matches, or the
 *     crew scans the one actually installed and says why (the office is told);
 *   · the old equipment coming out;
 *   · materials used vs planned (a reason whenever they differ) and materials
 *     the crew needed that weren't planned;
 *   · add-ons sold on site at the pricebook price, signed by the customer on
 *     the iPad → an approved change order credited to the installer
 *     (decision 3: commission is a % of the add-on price, paid after review).
 *
 * Competitor frame: ServiceTitan (sold estimate on the tablet, serial scan,
 * "Sold by" on anything added on site → spiffs, customer signs), Jobber (a
 * crew role without costs), CompanyCam / Housecall Pro (checklists).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { JobEquipment } from "./jobs";

/* ------------------------------------------------------------------ money */

export interface JobMoney {
  contract: number;
  /** Every approved change order that prints (office + field). */
  changes: number;
  /** The add-ons sold on site (part of `changes`). */
  field_changes: number;
  field_changes_n: number;
  /** Custom invoice lines the office added. */
  custom: number;
  total: number;
  /** Every payment on the job's invoices — the deposit included. */
  paid: number;
  deposit_billed: number;
  /** A deposit invoice that still has money owing. */
  deposit_open: number;
  balance: number;
  /* FIELD-PAY-01 (job_money v2) */
  /** Paid for the job but not on an invoice yet (the field's collection, a lender's funding). */
  held: number;
  /** How they're paying — from the sale, changeable by the office. */
  pay_plan: "full" | "financing" | "bill_after";
  fin_status: "applied" | "approved" | "funded" | "declined" | null;
  fin_amount: number | null;
  fin_approval_no: string | null;
  fin_plan_name: string | null;
  fin_plan_number: string | null;
  /** Financing approved but not yet funded — the lender's share, not the customer's. */
  financed: number;
  /** What the customer owes NOW (never negative). */
  due: number;
  /** What the installer does: collect it, nothing's due, wait on the lender, the office bills, or the job's closed. */
  collect: "ok" | "paid" | "financing_pending" | "bill_after" | "closed";
}

export function useJobMoney(jobId: string | null) {
  return useQuery({
    queryKey: ["job_money", jobId],
    enabled: !!jobId,
    staleTime: 15 * 1000,
    queryFn: async (): Promise<JobMoney | null> => {
      const { data, error } = await supabase.rpc("job_money", { p_job_id: jobId! });
      if (error) throw error;
      if (!data) return null;
      const d = data as Record<string, unknown>;
      const n = (k: string) => Number(d[k] ?? 0) || 0;
      const t = (k: string) => (d[k] == null || d[k] === "" ? null : String(d[k]));
      return {
        contract: n("contract"), changes: n("changes"), field_changes: n("field_changes"), field_changes_n: n("field_changes_n"),
        custom: n("custom"), total: n("total"), paid: n("paid"), deposit_billed: n("deposit_billed"),
        deposit_open: n("deposit_open"), balance: n("balance"),
        held: n("held"),
        pay_plan: (t("pay_plan") ?? "full") as JobMoney["pay_plan"],
        fin_status: t("fin_status") as JobMoney["fin_status"],
        fin_amount: d.fin_amount == null ? null : n("fin_amount"),
        fin_approval_no: t("fin_approval_no"),
        fin_plan_name: t("fin_plan_name"),
        fin_plan_number: t("fin_plan_number"),
        financed: n("financed"),
        due: n("due"),
        collect: (t("collect") ?? "ok") as JobMoney["collect"],
      };
    },
  });
}

function useInvalidateJobField() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["job_equipment"] });
    void qc.invalidateQueries({ queryKey: ["job_materials"] });
    void qc.invalidateQueries({ queryKey: ["change_orders"] });
    void qc.invalidateQueries({ queryKey: ["job_money"] });
    void qc.invalidateQueries({ queryKey: ["job_removed_equipment"] });
    void qc.invalidateQueries({ queryKey: ["job_refrigerant"] });
    void qc.invalidateQueries({ queryKey: ["job_checks"] });
    void qc.invalidateQueries({ queryKey: ["internal_notes"] });
    void qc.invalidateQueries({ queryKey: ["jobs"] });
  };
}

async function me(): Promise<string | null> {
  const { data } = await supabase.auth.getUser();
  return data?.user?.email ?? null;
}

async function noteToOffice(jobId: string, body: string, author: string | null) {
  await supabase.from("pegasus_internal_note").insert({
    entity_type: "job", entity_id: jobId, body, author_name: author, author_email: await me(),
  });
}

/* ------------------------------------------------------ equipment going in */

export const SERIAL_REASONS = [
  "Warehouse sent a different unit",
  "Serial on file was a typo",
  "Swapped out a damaged unit",
  "Other",
];

/** The crew confirms a unit is the one going in. With `serial` / `model` /
 *  `manufacturer`: what is printed on the unit actually installed. When the
 *  serial or the MODEL differs from what the office sold / the warehouse
 *  logged, the old values are kept (serial_before / model_before /
 *  manufacturer_before) with the reason, and the office gets a note. The
 *  warehouse's pull stamp is never rewritten here. */
export function useConfirmUnitOnSite() {
  const inval = useInvalidateJobField();
  return useMutation({
    mutationFn: async (v: {
      unit: JobEquipment; serial?: string | null; model?: string | null; manufacturer?: string | null;
      reason?: string | null; photoPath?: string | null; techFirst: string | null;
      /** REFRIG-01: read off the tag (only written when the tag printed it). */
      refrigerant?: string | null; factoryChargeOz?: number | null; factoryLinesetFt?: number | null;
    }) => {
      const email = await me();
      const norm = (x: string | null | undefined) => (x ?? "").trim();
      const next = norm(v.serial).toUpperCase() || null;
      const was = norm(v.unit.serial).toUpperCase() || null;
      const patch: Record<string, unknown> = {
        install_confirmed_by: email,
        install_confirmed_at: new Date().toISOString(),
      };
      const serialChanged = !!next && next !== was;
      // A blank on the job just gets filled in; wording ("LENNOX INDUSTRIES
      // INC." vs "Lennox", dashes in a model) is never a different unit.
      const modelChanged = !!norm(v.model) && !!norm(v.unit.model) && !sameModel(v.model, v.unit.model);
      const brandChanged = !!norm(v.manufacturer) && !!norm(v.unit.manufacturer) && !sameBrand(v.manufacturer, v.unit.manufacturer);
      if (!norm(v.unit.model) && norm(v.model)) patch.model = norm(v.model);
      if (!norm(v.unit.manufacturer) && norm(v.manufacturer)) patch.manufacturer = tidyBrand(v.manufacturer);
      const reason = norm(v.reason) || "Other";
      if (norm(v.refrigerant)) patch.refrigerant = norm(v.refrigerant);
      if (v.factoryChargeOz != null) patch.factory_charge_oz = v.factoryChargeOz;
      if (v.factoryLinesetFt != null) patch.factory_lineset_ft = v.factoryLinesetFt;
      if (serialChanged) {
        patch.serial = next;
        if (v.photoPath) patch.serial_photo_path = v.photoPath;
        if (was) {
          patch.serial_before = was;
          patch.serial_change_reason = reason;
        }
      }
      if (modelChanged || brandChanged) {
        // Keep what was SOLD the first time only — a second swap on site
        // still compares against the office's unit.
        if (modelChanged) {
          patch.model = norm(v.model);
          if (!v.unit.model_before) patch.model_before = v.unit.model;
        }
        if (brandChanged) {
          patch.manufacturer = tidyBrand(v.manufacturer);
          if (!v.unit.manufacturer_before) patch.manufacturer_before = v.unit.manufacturer;
        }
        patch.serial_change_reason = reason;
      }
      const { error } = await supabase.from("pegasus_job_equipment").update(patch).eq("id", v.unit.id);
      if (error) throw error;
      if ((serialChanged && was) || modelChanged || brandChanged) {
        const sold = [v.unit.manufacturer, v.unit.model].filter(Boolean).join(" ") || "the sold unit";
        const put = [brandChanged ? tidyBrand(v.manufacturer) : v.unit.manufacturer, modelChanged ? norm(v.model) : v.unit.model].filter(Boolean).join(" ");
        const parts: string[] = [];
        if (modelChanged || brandChanged) parts.push(`the job had ${sold}, the crew installed ${put || "a different unit"}`);
        if (serialChanged && was) parts.push(`the warehouse logged S/N ${was}, the unit says ${next}`);
        else if (serialChanged) parts.push(`S/N ${next}`);
        await noteToOffice(v.unit.job_id,
          `Different unit installed (${v.unit.category || "equipment"}): ${parts.join("; ")}. Why: ${reason}.`,
          v.techFirst);
      }
    },
    onSuccess: inval,
  });
}

/** Undo the crew's confirmation (a serial the crew changed goes back to the warehouse's). */
export function useUnconfirmUnit() {
  const inval = useInvalidateJobField();
  return useMutation({
    mutationFn: async (unit: JobEquipment) => {
      const patch: Record<string, unknown> = { install_confirmed_by: null, install_confirmed_at: null };
      if (unit.serial_before) {
        patch.serial = unit.serial_before;
        patch.serial_before = null;
      }
      if (unit.model_before) { patch.model = unit.model_before; patch.model_before = null; }
      if (unit.manufacturer_before) { patch.manufacturer = unit.manufacturer_before; patch.manufacturer_before = null; }
      if (unit.serial_before || unit.model_before || unit.manufacturer_before) patch.serial_change_reason = null;
      const { error } = await supabase.from("pegasus_job_equipment").update(patch).eq("id", unit.id);
      if (error) throw error;
    },
    onSuccess: inval,
  });
}

/** Accessories: a plain "went in" tick. */
export function useToggleUnitInstalled() {
  const inval = useInvalidateJobField();
  return useMutation({
    mutationFn: async (v: { id: string; on: boolean }) => {
      const email = await me();
      const { error } = await supabase.from("pegasus_job_equipment").update({
        install_confirmed_by: v.on ? email : null,
        install_confirmed_at: v.on ? new Date().toISOString() : null,
      }).eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: inval,
  });
}

/* ------------------------------------------------------- data-tag reading */

/** What read-serial (v5) reads off a data tag. */
export interface TagRead {
  serial: string; model: string; manufacturer: string;
  /** furnace | air_conditioner | heat_pump | evaporator_coil | air_handler | water_heater | boiler | mini_split | other | "" */
  equipment_type: string;
  manufacture_year: number | null;
  /** read-serial v6 (REFRIG-01): what the plate prints — "" / null when it doesn't. */
  refrigerant: string;
  factory_charge_oz: number | null;
  factory_lineset_ft: number | null;
}
const fileB64 = (file: File) =>
  new Promise<string>((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result).split(",")[1] || "");
    r.onerror = rej;
    r.readAsDataURL(file);
  });
/** Read a data-tag photo. Never throws — an unreadable tag comes back blank. */
export async function readDataTag(file: File): Promise<TagRead> {
  const blank: TagRead = { serial: "", model: "", manufacturer: "", equipment_type: "", manufacture_year: null, refrigerant: "", factory_charge_oz: null, factory_lineset_ft: null };
  try {
    const { data, error } = await supabase.functions.invoke("read-serial", { body: { image_base64: await fileB64(file), mime: file.type || "image/jpeg" } });
    if (error || !data) return blank;
    const d = data as Partial<TagRead>;
    return {
      serial: String(d.serial ?? "").toUpperCase(), model: String(d.model ?? ""), manufacturer: String(d.manufacturer ?? ""),
      equipment_type: String(d.equipment_type ?? ""), manufacture_year: typeof d.manufacture_year === "number" ? d.manufacture_year : null,
      refrigerant: String(d.refrigerant ?? ""),
      factory_charge_oz: typeof d.factory_charge_oz === "number" ? d.factory_charge_oz : null,
      factory_lineset_ft: typeof d.factory_lineset_ft === "number" ? d.factory_lineset_ft : null,
    };
  } catch {
    return blank;
  }
}
/* Data tags print the maker's legal name — "LENNOX INDUSTRIES INC.",
 * "CARRIER CORPORATION", "GOODMAN MANUFACTURING COMPANY, L.P." — while the
 * job says "Lennox". These treat those as the same brand (so a crew isn't
 * asked "why a different unit?" over wording) and file the short name. */
const BRAND_NOISE = new Set(["INDUSTRIES", "INDUSTRY", "INC", "INCORPORATED", "CORPORATION", "CORP", "COMPANY", "CO", "LLC", "LP", "LTD", "MANUFACTURING", "MFG", "US", "USA", "HVAC"]);
const brandWords = (s: string | null | undefined) =>
  (s ?? "").toUpperCase().replace(/\./g, "").replace(/[^A-Z0-9&]+/g, " ").trim().split(/\s+/).filter((w) => w && !BRAND_NOISE.has(w));
/** "LENNOX INDUSTRIES INC." → "LENNOX" — compare brands on this. */
export const brandKey = (s: string | null | undefined) => brandWords(s).join(" ");
/** Same maker? Blank on either side never counts as different. */
export function sameBrand(a: string | null | undefined, b: string | null | undefined): boolean {
  const x = brandKey(a), y = brandKey(b);
  if (!x || !y) return true;
  return x === y || x.startsWith(`${y} `) || y.startsWith(`${x} `);
}
/** Same model? Ignores dashes, spaces and case ("58STA070-1-10" = "58STA070110"). Blank never differs. */
export function sameModel(a: string | null | undefined, b: string | null | undefined): boolean {
  const k = (s: string | null | undefined) => (s ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  return !k(a) || !k(b) || k(a) === k(b);
}
/** The tag's maker name, filed short: "LENNOX INDUSTRIES INC." → "Lennox", "A.O. SMITH CORP" → "A.O. Smith". */
export function tidyBrand(raw: string | null | undefined): string {
  const kept = (raw ?? "").trim().replace(/[,.]+$/, "").split(/\s+/).filter((w) => w && !BRAND_NOISE.has(w.toUpperCase().replace(/[^A-Z0-9&]/g, "")));
  const s = kept.join(" ").replace(/[,]+$/, "").trim();
  if (!s || s !== s.toUpperCase()) return s; // already mixed case — keep it
  return s.split(" ").map((w) => (w.replace(/[^A-Z]/g, "").length > 3 ? w[0] + w.slice(1).toLowerCase() : w)).join(" ");
}

/** read-serial's type → the "Coming Out" kind chips. */
export const KIND_FROM_TAG: Record<string, string> = {
  furnace: "Furnace", air_conditioner: "Air Conditioner", heat_pump: "Heat Pump", evaporator_coil: "Coil",
  air_handler: "Air Handler", water_heater: "Water Heater", boiler: "Boiler", mini_split: "Other", other: "Other",
};

/* ---------------------------------------------------- old units coming out */

export interface RemovedUnit {
  id: string;
  job_id: string;
  dispatch_id: string | null;
  category: string | null;
  brand: string | null;
  model: string | null;
  serial: string | null;
  year_made: number | null;
  disposition: "haul_away" | "left_on_site";
  from_equipment: string | null;
  notes: string | null;
  /** The old unit's data-tag photo (a pegasus_file on the job). */
  tag_file_id?: string | null;
  /** REFRIG-01: the classifier's key (DB trigger) + what its plate says it held. */
  equip_key?: string | null;
  refrigerant?: string | null;
  nameplate_charge_oz?: number | null;
  created_by: string | null;
  created_at: string;
}
export const REMOVED_KINDS = ["Furnace", "Air Conditioner", "Heat Pump", "Coil", "Air Handler", "Boiler", "Water Heater", "Other"];
export const DISPOSITION_LABEL: Record<RemovedUnit["disposition"], string> = {
  haul_away: "We hauled it away",
  left_on_site: "Left on site",
};

export function useRemovedEquipment(jobId: string | null) {
  return useQuery({
    queryKey: ["job_removed_equipment", jobId],
    enabled: !!jobId,
    queryFn: async (): Promise<RemovedUnit[]> => {
      const { data, error } = await supabase.from("pegasus_job_removed_equipment").select("*")
        .eq("job_id", jobId!).order("created_at").order("id");
      if (error) throw error;
      return (data ?? []) as RemovedUnit[];
    },
  });
}

export function useAddRemovedEquipment() {
  const inval = useInvalidateJobField();
  return useMutation({
    mutationFn: async (v: Omit<RemovedUnit, "id" | "created_by" | "created_at">): Promise<string> => {
      const clean = (s: string | null) => (s ?? "").trim() || null;
      const { data, error } = await supabase.from("pegasus_job_removed_equipment").insert({
        job_id: v.job_id, dispatch_id: v.dispatch_id, category: clean(v.category), brand: clean(v.brand),
        model: clean(v.model), serial: clean(v.serial)?.toUpperCase() ?? null,
        year_made: v.year_made && v.year_made >= 1950 && v.year_made <= 2100 ? v.year_made : null,
        disposition: v.disposition, from_equipment: v.from_equipment, notes: clean(v.notes),
        tag_file_id: v.tag_file_id ?? null, created_by: await me(),
        refrigerant: clean(v.refrigerant ?? null), nameplate_charge_oz: v.nameplate_charge_oz ?? null,
      }).select("id").single();
      if (error) throw error;
      return (data as { id: string }).id;
    },
    onSuccess: inval,
  });
}

export function useDeleteRemovedEquipment() {
  const inval = useInvalidateJobField();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("pegasus_job_removed_equipment").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: inval,
  });
}

/** What's on file at the address — offered as one-tap picks for the old unit. */
export interface UnitOnFile {
  key: string; category: string | null; brand: string | null; model: string | null;
  serial: string | null; year: number | null;
}
export function useUnitsOnFile(custno: string | null, locno: string | null) {
  return useQuery({
    queryKey: ["units_on_file", custno, locno],
    enabled: !!custno,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<UnitOnFile[]> => {
      let q = supabase.from("mirror_equipment").select("counter, mfg, model, serial, eq_type, install_date").eq("custno", custno!);
      if (locno) q = q.eq("locno", locno);
      let p = supabase.from("pegasus_equipment").select("id, brand, model, serial, category, install_date, status, job_id").eq("custno", custno!);
      if (locno) p = p.eq("locno", locno);
      const [esc, peg] = await Promise.all([q.limit(20), p.limit(20)]);
      const yr = (d: string | null) => (d ? Number(String(d).slice(0, 4)) || null : null);
      const escRows = ((esc.data ?? []) as Array<{ counter: string | number; mfg: string | null; model: string | null; serial: string | null; eq_type: string | null; install_date: string | null }>)
        .map((r) => ({ key: `esc:${r.counter}`, category: r.eq_type, brand: r.mfg, model: r.model, serial: r.serial, year: yr(r.install_date) }));
      const pegRows = ((peg.data ?? []) as Array<{ id: string; brand: string | null; model: string | null; serial: string | null; category: string | null; install_date: string | null; status: string | null }>)
        .filter((r) => (r.status ?? "active") === "active")
        .map((r) => ({ key: `peg:${r.id}`, category: r.category, brand: r.brand, model: r.model, serial: r.serial, year: yr(r.install_date) }));
      return [...pegRows, ...escRows].filter((r) => r.model || r.serial || r.brand);
    },
  });
}

/* -------------------------------------------------------------- materials */

export const MATERIAL_REASONS = [
  "Longer run than planned",
  "Shorter run — didn't need it all",
  "Damaged — had to replace",
  "Code / inspector required it",
  "Customer asked for a change",
];

/** Used ≠ planned needs a reason; used = planned clears it. */
export function useSetMaterialUsed() {
  const inval = useInvalidateJobField();
  return useMutation({
    mutationFn: async (v: { id: string; planned: number; used: number; reason: string | null }) => {
      const same = Math.abs(v.used - v.planned) < 1e-9;
      if (!same && !(v.reason ?? "").trim()) throw new Error("REASON_REQUIRED");
      const { error } = await supabase.from("pegasus_job_material").update({
        qty_actual: same ? null : v.used,
        change_reason: same ? null : (v.reason ?? "").trim(),
        changed_by: await me(),
        changed_at: new Date().toISOString(),
      }).eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: inval,
  });
}

export function useAddFieldMaterial() {
  const inval = useInvalidateJobField();
  return useMutation({
    mutationFn: async (v: { jobId: string; itemId: string | null; description: string | null; qty: number; unit: string | null; reason: string }) => {
      const { data, error } = await supabase.rpc("add_field_material", {
        p_job_id: v.jobId, p_item_id: v.itemId, p_description: v.description, p_qty: v.qty, p_unit: v.unit, p_reason: v.reason,
      });
      if (error) throw new Error(fieldError(error.message));
      return data as string;
    },
    onSuccess: inval,
  });
}

/** Only the crew's own field-added lines come off from the iPad. */
export function useRemoveFieldMaterial() {
  const inval = useInvalidateJobField();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("pegasus_job_material").delete().eq("id", id).eq("source", "field");
      if (error) throw error;
    },
    onSuccess: inval,
  });
}

/* ------------------------------------------------------ add-ons sold on site */

export function useSellFieldAddon() {
  const inval = useInvalidateJobField();
  return useMutation({
    mutationFn: async (v: {
      jobId: string; itemId: string | null; description: string | null; qty: number; unitPrice: number | null;
      signature: string; signedName: string | null; dispatchId: string | null; soldByEmpno: string | null; soldByName: string | null;
    }) => {
      const { data, error } = await supabase.rpc("sell_field_addon", {
        p_job_id: v.jobId, p_item_id: v.itemId, p_description: v.description, p_qty: v.qty, p_unit_price: v.unitPrice,
        p_signature: v.signature, p_signed_name: v.signedName, p_dispatch_id: v.dispatchId,
        p_sold_by_empno: v.soldByEmpno, p_sold_by_name: v.soldByName,
      });
      if (error) throw new Error(fieldError(error.message));
      return data as { id: string; amount: number; description: string };
    },
    onSuccess: inval,
  });
}

/** The database's short codes → words the crew can act on. */
export function fieldError(msg: string | null | undefined): string {
  const m = msg ?? "";
  if (/JOB_CLOSED/.test(m)) return "This job is closed — ask the office.";
  if (/NC_JOB/.test(m)) return "Builder extras go through the builder's super, not the homeowner.";
  if (/SIGNATURE_REQUIRED/.test(m)) return "The customer needs to sign first.";
  if (/NO_PRICE/.test(m)) return "That item has no price in the pricebook — ask the office to price it.";
  if (/REASON_REQUIRED/.test(m)) return "Pick a reason first.";
  if (/DESCRIPTION_REQUIRED/.test(m)) return "Say what it is first.";
  if (/BAD_QTY/.test(m)) return "Check the quantity.";
  if (/ITEM_NOT_FOUND/.test(m)) return "That item isn't in the pricebook anymore.";
  return "Didn't save — check the signal and try again.";
}
